type PointerPosition = { x: number; y: number };

type DocumentTrackingState = {
  refCount: number;
  abortController: AbortController;
};

const pointerSamples = new Map<Document, PointerPosition>();
const documentTracking = new Map<Document, DocumentTrackingState>();

// Used by usePopoverState when resumeHoverOnTargetMount is enabled. After a virtualized row
// remounts, :hover is not applied to the new DOM node even though the pointer never moved.
// elementFromPoint is the fallback for deciding whether to re-schedule a delayed hover open.

function createPointerMoveHandler(doc: Document): (event: Event) => void {
  return event => {
    if (!documentTracking.has(doc)) {
      return;
    }

    const { clientX, clientY } = event as MouseEvent;
    pointerSamples.set(doc, { x: clientX, y: clientY });
  };
}

function createPointerLeaveHandler(doc: Document): () => void {
  return () => {
    pointerSamples.delete(doc);
  };
}

function attachListeners(doc: Document, controller: AbortController): void {
  const { signal } = controller;
  const options = { capture: true, passive: true, signal } as const;
  doc.addEventListener('pointermove', createPointerMoveHandler(doc), options);
  doc.addEventListener('pointerleave', createPointerLeaveHandler(doc), options);
}

function createAbortController(doc: Document): AbortController {
  const AbortControllerCtor = doc.defaultView?.AbortController ?? globalThis.AbortController;
  return new AbortControllerCtor();
}

export function recordPointerPosition(x: number, y: number, doc: Document = document): void {
  pointerSamples.set(doc, { x, y });
}

export function resetPointerPosition(): void {
  pointerSamples.clear();
}

export function ensurePointerPositionTracking(doc: Document): () => void {
  let state = documentTracking.get(doc);
  if (state == null) {
    const abortController = createAbortController(doc);
    state = { refCount: 0, abortController };
    documentTracking.set(doc, state);
    attachListeners(doc, abortController);
  }

  state.refCount += 1;

  let released = false;

  return () => {
    if (released) {
      return;
    }
    released = true;

    const current = documentTracking.get(doc);
    if (current == null) {
      return;
    }

    current.refCount -= 1;
    if (current.refCount > 0) {
      return;
    }

    current.abortController.abort();
    documentTracking.delete(doc);
  };
}

/** Test-only: drop capture listeners so pointer tracking does not leak across files. */
export function stopPointerPositionTracking(): void {
  for (const state of documentTracking.values()) {
    state.abortController.abort();
  }
  documentTracking.clear();
  pointerSamples.clear();
}

export function isPointerOverElement(element: HTMLElement): boolean {
  if (element.matches(':hover')) {
    return true;
  }

  const ownerDocument = element.ownerDocument;
  const lastPosition = pointerSamples.get(ownerDocument);
  if (lastPosition == null) {
    return false;
  }

  const elementFromPoint = ownerDocument.elementFromPoint;
  if (elementFromPoint == null) {
    return false;
  }

  const elementAtPoint = elementFromPoint.call(ownerDocument, lastPosition.x, lastPosition.y);

  return elementAtPoint != null && element.contains(elementAtPoint);
}
